<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import Dropdown from "$app/components/Dropdown.svelte"
  import {
    PartyPopper,
    ThumbsUp,
    MessageSquare,
    FileText,
    EllipsisVertical
  } from "lucide-svelte"
  import { timeAgo, badgeImage, titleize } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let props = $props()

  let notification: NotificationType = $state(props.notification)

  let openDropdown = $state(false)

  async function toggleMarkAsRead(): Promise<void> {
    openDropdown = false
    try {
      let action = notification.read_at ? "mark_as_unread" : "mark_as_read"
      const resp = await xhr.patch(
        `/api/v1/notifications/${notification.id}/${action}.json`
      )

      notification = { ...notification, ...resp.data.notification }

      const objIndex = appStore.notifications.findIndex(
        (obj) => obj.id === notification.id
      )
      appStore.notifications[objIndex] = notification
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your notification", "error")
    }
  }

  function verifyNotification(): boolean {
    if (notification.type === "RecognitionNotifier::Notification") {
      return notification.event.params.plan && notification.event.record
    }
    if (notification.type === "RecognitionCommentNotifier::Notification") {
      return (
        notification.event.params.recognition && notification.event.params.user
      )
    }
    if (notification.type === "RecognitionReactionNotifier::Notification") {
      return (
        notification.event.params.recognition && notification.event.params.user
      )
    }
    if (notification.type === "ForumPostNotifier::Notification") {
      return notification.event.record && notification.event.params.user
    }
    if (notification.type === "ForumPostCommentNotifier::Notification") {
      return (
        notification.event.params.forum_post && notification.event.params.user
      )
    }
    if (notification.type === "ForumPostReactionNotifier::Notification") {
      return (
        notification.event.params.forum_post && notification.event.params.user
      )
    }
    if (notification.type === "BadgeNotifier::Notification") {
      return (
        notification.event.params.badge &&
        notification.event.params.plan &&
        notification.event.record
      )
    }
    return false
  }

  function visitNotification(): void {
    switch (notification.type) {
      case "RecognitionNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.plan.id}/recognitions/${notification.event.record.id}?notification_id=${notification.id}`
        break
      case "RecognitionCommentNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.recognition.plan_id}/recognitions/${notification.event.params.recognition.id}?notification_id=${notification.id}`
        break
      case "RecognitionReactionNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.recognition.plan_id}/recognitions/${notification.event.params.recognition.id}?notification_id=${notification.id}`
        break
      case "ForumPostNotifier::Notification":
        window.location.href = `/plans/${notification.event.record.plan_id}/community_posts/${notification.event.record.id}?notification_id=${notification.id}`
        break
      case "ForumPostCommentNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.forum_post.plan_id}/community_posts/${notification.event.params.forum_post.id}?notification_id=${notification.id}`
        break
      case "ForumPostReactionNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.forum_post.plan_id}/community_posts/${notification.event.params.forum_post.id}?notification_id=${notification.id}`
        break
      case "BadgeNotifier::Notification":
        window.location.href = `/plans/${notification.event.params.plan.id}/accomplishments?notification_id=${notification.id}`
        break
      default:
        break
    }
  }

  const [ImgIcon, msg] = $derived(
    {
      "RecognitionNotifier::Notification": [
        PartyPopper,
        "sent you a recognition!"
      ],
      "RecognitionCommentNotifier::Notification": [
        MessageSquare,
        "commented on your recognition."
      ],
      "RecognitionReactionNotifier::Notification": [
        ThumbsUp,
        "reacted to your recognition."
      ],
      "ForumPostNotifier::Notification": [FileText, "posted to the Community."],
      "ForumPostCommentNotifier::Notification": [
        MessageSquare,
        "commented on your Community post."
      ],
      "ForumPostReactionNotifier::Notification": [
        ThumbsUp,
        "reacted to your Community post."
      ],
      "BadgeNotifier::Notification": [PartyPopper, ""]
    }[notification.type] || [ThumbsUp, ""]
  )

  const user = $derived(
    notification.event.params.user ||
      notification.event.params.sender ||
      notification.event.record
  )
</script>

{#if verifyNotification()}
  <li class="hover:bg-gray-50 cursor-pointer">
    <div class="group relative flex items-center px-5 py-6">
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <div
        class="relative flex min-w-0 flex-1 items-center"
        role="button"
        tabindex="0"
        onclick={visitNotification}
        onkeydown={(e) => e.key === "Enter" && visitNotification()}
      >
        <span class="relative inline-block flex-shrink-0">
          {#if notification.type === "BadgeNotifier::Notification"}
            <img
              class="size-12 rounded-full shadow"
              src={badgeImage(
                notification.event.params.badge.name,
                notification.event.params.badge.level
              )}
              alt={notification.event.params.badge.name}
            />
          {:else}
            <img
              class="size-10 rounded-full shadow"
              src={`/api/v1/users/${user.id}/avatar_img`}
              alt={user.name}
            />

            <span
              class="bg-lwp-accent p-0.5 rounded-full w-5 h-5 absolute -bottom-1 -right-2 ring-1 ring-white flex items-center justify-center"
            >
              {#if ImgIcon}
                <ImgIcon class="size-4 text-white block rounded-full" />
              {/if}
            </span>
          {/if}
        </span>

        <div class="ml-4">
          {#if notification.type === "BadgeNotifier::Notification"}
            <div
              class="text-sm {notification.read_at
                ? 'text-gray-500'
                : 'text-gray-900'}"
            >
              <span class="font-medium">Congratulations!</span> You were awarded
              the badge
              <span class="font-bold">
                {#if notification.event.params.badge.level > 0}
                  {titleize(notification.event.params.badge.name)}
                  {notification.event.params.badge.level}.
                {:else}
                  {titleize(notification.event.params.badge.name)}.
                {/if}
              </span>
            </div>
          {:else}
            <div>
              <span class="text-sm font-semibold whitespace-nowrap">
                {user.name}
              </span>
              <span
                class="text-sm {notification.read_at
                  ? 'text-gray-500'
                  : 'text-gray-900'}"
              >
                {msg}
              </span>
            </div>
          {/if}
          <div
            class="text-xs {notification.read_at
              ? 'text-gray-500'
              : 'text-blue-700'}"
          >
            {timeAgo(notification.created_at)}
          </div>
        </div>
      </div>
      <div class="relative ml-2 flex items-center justify-start">
        <div class="flex justify-between mt-1 text-gray-500 text-sm pr-3">
          <Dropdown bind:open={openDropdown}>
            {#snippet button()}
              <span data-name="task-context-menu">
                <EllipsisVertical />
                <!-- class="text-gray-600 fill-current w-5 h-5"
              /> -->
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute -right-2 mt-0 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                role="menu"
              >
                <div class="py-1" role="none">
                  <button
                    onclick={() => {
                      toggleMarkAsRead()
                    }}
                    class="btn-dropdown"
                    role="menuitem"
                  >
                    {#if notification.read_at}
                      Mark as unread
                    {:else}
                      Mark as read
                    {/if}
                  </button>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
        {#if !notification.read_at}
          <span
            class="bg-blue-800 block h-4 w-4 rounded-full ring-1 ring-white shadow-lg"
            aria-hidden="true"
          ></span>
        {:else}
          <span class="bg-transparent block h-4 w-4"></span>
        {/if}
      </div>
    </div>
  </li>
{/if}
